import {
  faBallotCheck,
  faGauge,
  faLink,
  faUsers,
  faWrench,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router';
import LoadingScreen from '../../components/muiLegacy/LoadingScreen/LoadingScreen';
import VoxelPageContainer from '../../components/voxel/Containers/VoxelPageContainer';
import VoxelMenu, { MenuItem } from '../../components/voxel/Menu/VoxelMenu';
import VoxelText from '../../components/voxel/Text/VoxelText';
import VoxelAvatar from '../../components/voxel/VoxelAvatar';
import { usersApiService } from '../../graphql/users/usersApiService';
import useIsScrolling from '../../logic/otherUtilities/useIsScrolling';
import StartPageAppBar from '../StartPage/components/StartPageAppBar';
import SubPageWrapper from './Components/SubPageWrapper';
import TeamConnectionsPage from './TeamConnectionsPage/TeamConnectionsPage';
import MembersPage from './MembersPage/MembersPage';
import SubscriptionPage from './SubscriptionPage/SubscriptionPage';
import TeamSettingsPage from './TeamSettingsPage';
import UsagePage from './UsagePage/UsagePage';

export interface TeamPageProps { }

export enum TeamMenuChoices {
  Settings = 'settings',
  Usage = 'usage',
  Members = 'members',
  Connections = 'connections',
  Subscription = 'subscription',
}

export type ReloadFunction = () => Promise<unknown>;

export const TeamPage: React.FC<TeamPageProps> = ({ }) => {
  const isScrolling = useIsScrolling();

  // Get user
  const {
    user,
    team,
    subscriptionEvents,
    query: userQuery,
    availableUsers,
  } = usersApiService.queries.useGetCurrentUser();

  const [reloadFunctions, setReloadFunctions] = useState<ReloadFunction[]>([]);
  const handleReload = useCallback(
    () => Promise.all(reloadFunctions.map((fn) => fn())),
    [reloadFunctions]
  );

  // Navigation
  const match = useRouteMatch();
  const history = useHistory();
  const goToTab = useMemo(() => {
    const action = (tab: TeamMenuChoices) => {
      history.push(`/team/${tab}`);
    };
    return action;
  }, [user?.team.id]);

  useEffect(() => {
    setReloadFunctions((prev) => [...prev, userQuery.refetch]);
  }, [userQuery.refetch, match.params]);

  // Rendering (no hooks after this)
  if (!user) {
    return <LoadingScreen />;
  }

  const menuItems: MenuItem[] = [
    {
      name: 'Settings',
      id: TeamMenuChoices.Settings,
      icon: <FontAwesomeIcon icon={faWrench} />,
      disabled: false,
    },
    {
      name: 'Members',
      id: TeamMenuChoices.Members,
      icon: <FontAwesomeIcon icon={faUsers} />,
      disabled: false,
    },
    {
      name: 'Team connections',
      id: TeamMenuChoices.Connections,
      icon: <FontAwesomeIcon icon={faLink} />,
      disabled: false,
    },

    {
      name: 'Usage',
      id: TeamMenuChoices.Usage,
      icon: <FontAwesomeIcon icon={faGauge} />,
      disabled: false,
    },
    {
      name: 'Subscription',
      id: TeamMenuChoices.Subscription,
      icon: <FontAwesomeIcon icon={faBallotCheck} />,
      disabled: false,
    },
  ];

  const path = '/team';
  const selectedId = (match.params as any).tab;

  return (
    <>
      <StartPageAppBar
        user={user}
        isScrolling={isScrolling}
        team={team}
        availableUsers={availableUsers}
      />
      <VoxelPageContainer>
        {/* Top section */}
        <div className={'flex flex-row gap-8 items-center flex-nowrap py-4'}>
          {/* Team avatar */}
          {!!user.team.name?.length && <VoxelAvatar name={user.team.name} size={'xxl'} />}
          {/* Team information */}
          <div className="flex flex-col gap-1">
            {!!user.team.name?.length && (
              <VoxelText variant={'title2'}>{user.team.name}</VoxelText>
            )}
            <VoxelText variant={'bread-medium'}>Team account</VoxelText>
          </div>
        </div>
        {/* Bottom section */}
        <div className="mt-12 grid grid-cols-12 gap-x-12">
          {/* Bottom section - menu */}
          <VoxelMenu
            className="w-full col-span-4 md:col-span-3 2xl:col-span-2"
            menuListItems={menuItems}
            selectedId={selectedId}
            setSelectedId={goToTab}
          />
          {/* Bottom section - content */}
          <div className="flex-grow col-span-8 md:col-span-9 2xl:col-span-10">
            <Switch>
              <Route
                path={path + '/' + TeamMenuChoices.Settings}
                render={() => (
                  <TeamSettingsPage user={user} team={team} reload={userQuery.refetch} />
                )}
              />
              <Route
                path={path + '/' + TeamMenuChoices.Members}
                render={() => <MembersPage handleReload={handleReload} user={user} />}
              />
              <Route
                path={path + '/' + TeamMenuChoices.Connections}
                render={() => (
                  <TeamConnectionsPage
                    team={team}
                    user={user}
                    availableUsers={availableUsers}
                    reload={userQuery.refetch}
                  />
                )}
              />
              <Route
                path={path + '/' + TeamMenuChoices.Usage}
                render={() => <UsagePage team={user.team} />}
              />
              <Route
                path={path + '/' + TeamMenuChoices.Subscription}
                render={() => (
                  <SubPageWrapper title="Subscription">
                    <SubscriptionPage
                      team={user.team}
                      user={user}
                      reload={userQuery.refetch}
                      subscriptionEvents={subscriptionEvents}
                    />
                  </SubPageWrapper>
                )}
              />
            </Switch>
          </div>
        </div>
      </VoxelPageContainer>
    </>
  );
};

export default TeamPage;
